/**
 * L5 — the four spot illustrations for the app's empty states.
 *
 * ===========================================================================
 * WHAT A SPOT IS, AND WHAT IT IS NOT
 * ===========================================================================
 * It is a quiet mark, not artwork. An empty state's job is to say what is
 * missing and offer the one action that fixes it; the drawing exists to stop the
 * screen reading as an error, and then to get out of the way. So each of these
 * is one idea in three to five shapes, drawn on a 64-unit grid in a single
 * stroke weight, and none of them is bigger than the sentence beside it.
 *
 * HARD RULE 6 IS SATISFIED STRUCTURALLY, NOT BY PROMISE:
 *  · Hand-rolled inline SVG. No dependency, no asset file, no sprite sheet, no
 *    fetch — the mark cannot fail to load, because there is nothing to load.
 *  · ≤ 6 drawn shapes each. The counts are 5 / 4 / 4 / 5 and are written above
 *    each drawing so a later edit has to notice the budget it is spending.
 *  · `currentColor` throughout, tint included (a filled shape is currentColor at
 *    a low opacity, never a second hex). One drawing therefore serves light AND
 *    dark mode: it inherits whatever the caller's text colour resolves to, so it
 *    can never be the one element that stayed light-mode grey on a dark page.
 *    This is also why there is no `fill="#…"` anywhere below — a literal hex
 *    here would be exactly the hardcoded colour hard rule 1 forbids.
 *
 * There is no `title`/`desc` and every spot is `aria-hidden`. The meaning lives
 * in the empty state's heading and its button; a screen reader that also
 * announced "illustration of an empty column" would be reading the decoration
 * and saying the same thing twice.
 */

import type { ReactNode } from 'react';

export const SPOT_NAMES = ['inquiries', 'column', 'search', 'activity'] as const;

export type SpotName = (typeof SPOT_NAMES)[number];

export interface SpotProps {
  name: SpotName;
  /**
   * A Tailwind size utility. It REPLACES the default rather than adding to it:
   * this repo has no `tailwind-merge` (see `lib/cn.ts`), so `cn('size-12',
   * 'size-16')` would emit both and let stylesheet order pick the winner.
   */
  className?: string;
}

/* Geometry — coordinates and stroke widths — is the drawing itself, so the
 * numbers below are literal. They are on a fixed 64×64 grid and scale with the
 * class the caller sets; none of them is a colour, a spacing step or a duration,
 * which are the three things the token rule governs. */
const SPOTS: Record<SpotName, ReactNode> = {
  /* Inquiries table, empty — a fresh sheet on top of the stack that is not
   * there yet. 5 shapes: stack tint, sheet, corner fold, two rules. */
  inquiries: (
    <>
      <rect
        x="16"
        y="14"
        width="28"
        height="36"
        rx="3"
        fill="currentColor"
        opacity="0.1"
        transform="rotate(-8 30 32)"
      />
      <path d="M24 12h14l8 8v32H24z" />
      <path d="M38 12v8h8" />
      <path d="M28 32h14" opacity="0.6" />
      <path d="M28 40h9" opacity="0.6" />
    </>
  ),

  /* Board column with no cards — the column is real, the cards are not. The two
   * dashed slots fade downward so the eye reads "room for more", not "broken".
   * 4 shapes: column, title bar, two card slots. */
  column: (
    <>
      <rect x="16" y="10" width="32" height="44" rx="5" />
      <rect
        x="22"
        y="17"
        width="12"
        height="4"
        rx="2"
        fill="currentColor"
        opacity="0.25"
        stroke="none"
      />
      <rect x="22" y="27" width="20" height="10" rx="3" strokeDasharray="3 3" opacity="0.55" />
      <rect x="22" y="41" width="20" height="8" rx="3" strokeDasharray="3 3" opacity="0.3" />
    </>
  ),

  /* Search with no results — a magnifier over nothing. The bar inside the lens
   * is the "nothing" and is the whole joke; without it this is just a search
   * icon. 4 shapes: lens tint, lens, empty bar, handle. */
  search: (
    <>
      <circle cx="27" cy="27" r="13" fill="currentColor" opacity="0.1" stroke="none" />
      <circle cx="27" cy="27" r="13" />
      <path d="M21 27h12" opacity="0.55" />
      <path d="M37 37l13 13" strokeLinecap="round" />
    </>
  ),

  /* Activity feed with nothing recent — a rail whose latest node is still
   * hollow. Reads as "the story continues here", which is true, rather than as
   * a flatline. 5 shapes: rail, logged node + its entry, pending node + its
   * entry. */
  activity: (
    <>
      <path d="M20 14v36" opacity="0.35" />
      <circle cx="20" cy="22" r="4" fill="currentColor" stroke="none" />
      <rect
        x="30"
        y="20"
        width="18"
        height="4"
        rx="2"
        fill="currentColor"
        opacity="0.25"
        stroke="none"
      />
      <circle cx="20" cy="42" r="4" opacity="0.55" />
      <rect
        x="30"
        y="40"
        width="12"
        height="4"
        rx="2"
        fill="currentColor"
        opacity="0.12"
        stroke="none"
      />
    </>
  ),
};

export function Spot({ name, className }: SpotProps): React.JSX.Element {
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      viewBox="0 0 64 64"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinejoin="round"
      className={className ?? 'size-12'}
    >
      {SPOTS[name]}
    </svg>
  );
}
